import {
  MODALITIES,
  THEME_TOKEN_VALUES,
  TOKENS_MANIFEST,
  createDefaultShellSection,
  projectOwnedBlob,
  type DesignSection,
  type PartOverride,
  type WidgetAppConfig,
} from '@pypestream/pype/customization';

/** UI-only shape — mirrors packages/apps/pype pype-designer designer-route. */
export interface OverrideDeclaration {
  id: string;
  property: string;
  value: string;
}

export interface OverrideGroup {
  id: string;
  component: string;
  part: string;
  declarations: OverrideDeclaration[];
}

export function newId(): string {
  return Math.random().toString(36).slice(2);
}

export function groupsToPartOverrides(groups: OverrideGroup[]): PartOverride[] {
  return groups
    .filter((g) => g.component && g.part)
    .map((g) => ({
      component: g.component,
      part: g.part,
      declarations: Object.fromEntries(
        g.declarations
          .filter((d) => d.property && d.value)
          .map((d) => [d.property, d.value])
      ),
    }));
}

/**
 * Guarantee a blank property/value pair at the END of the list.
 *
 * There is no "Add property" button: typing into the last pair grows the next
 * one, so a declaration costs a Tab rather than a click-then-Tab. Blank rows are
 * dropped by `groupsToPartOverrides` at save, so an always-present one persists
 * nothing.
 *
 * APPEND-ONLY, deliberately. Collapsing blank rows — even only trailing ones —
 * means clearing a field can delete the row under the cursor and take focus with
 * it, which is the classic failure of this pattern. Removing a row stays an
 * explicit gesture.
 */
export function withReadyRow(
  declarations: OverrideDeclaration[]
): OverrideDeclaration[] {
  const last = declarations[declarations.length - 1];
  if (last && !last.property && !last.value) return declarations;
  return [...declarations, { id: newId(), property: '', value: '' }];
}

export function partOverridesToGroups(
  overrides: PartOverride[]
): OverrideGroup[] {
  return overrides.map((o) => ({
    id: newId(),
    component: o.component,
    part: o.part,
    declarations: withReadyRow(
      Object.entries(o.declarations).map(([property, value]) => ({
        id: newId(),
        property,
        value,
      }))
    ),
  }));
}

// ─── Theme defaults: pulled from @pypestream/pype/customization ─────────────
//
// pype owns the DaisyUI dependency and exports per-theme token defaults via
// `THEME_TOKEN_VALUES`. No DaisyUI import on the Pro Studio side.
//
// We still convert oklch → hex at runtime (the design-system `<ColorPicker>`
// is TinyColor-based and doesn't read oklch). The conversion uses an
// off-DOM `<canvas>` so the browser parses the colour for us — works for
// every CSS colour function modern browsers understand.

/**
 * Convert a CSS color string (oklch/hex/rgb/hsl/named) to a `#rrggbb` hex
 * value. Uses an off-DOM canvas so the browser does the parsing — handles
 * oklch in modern browsers (Chrome 111+, Safari 16.4+, Firefox 113+).
 * Returns null if the browser cannot parse the value.
 */
function cssColorToHex(cssColor: string): string | null {
  if (!cssColor) return null;
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  // Set a sentinel first; if assignment is ignored (unsupported syntax),
  // we'll detect it and bail rather than returning the sentinel as a real
  // value.
  ctx.fillStyle = '#000001';
  ctx.fillStyle = cssColor;
  ctx.fillRect(0, 0, 1, 1);
  const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data;
  if (r === 0 && g === 0 && b === 1) return null;
  return (
    '#' +
    r.toString(16).padStart(2, '0') +
    g.toString(16).padStart(2, '0') +
    b.toString(16).padStart(2, '0')
  );
}

/**
 * Returns the default CSS variable values for the given DaisyUI theme.
 *
 * Color tokens (`--color-*`) are converted to `#rrggbb` so the design-system
 * `<ColorPicker>` (TinyColor under the hood, no oklch support) accepts them.
 * Non-color tokens are returned as their raw CSS value (e.g. `0.5rem`).
 *
 * Source: `THEME_TOKEN_VALUES` exported by `@pypestream/pype/customization`,
 * which pype regenerates from its DaisyUI dependency at codegen time.
 */
export function readThemeDefaults(themeName: string): Record<string, string> {
  const vars =
    (THEME_TOKEN_VALUES as Record<string, Record<string, string>>)[themeName] ??
    THEME_TOKEN_VALUES.light ??
    {};
  const result: Record<string, string> = {};
  for (const token of TOKENS_MANIFEST) {
    const raw = vars[token.variable];
    if (!raw) continue;
    if (token.type === 'color') {
      const hex = cssColorToHex(raw);
      if (hex) result[token.variable] = hex;
    } else {
      result[token.variable] = raw;
    }
  }
  return result;
}

export function buildDesignSection(config: {
  theme: string;
  tokens: Record<string, string>;
  overrideGroups: OverrideGroup[];
  rawCss: string;
}): DesignSection {
  return {
    theme: config.theme,
    tokens: config.tokens,
    partOverrides: groupsToPartOverrides(config.overrideGroups),
    rawCss: config.rawCss,
  };
}

/**
 * Normalise a freshly-loaded (or freshly-defaulted) `WidgetAppConfig`.
 *
 * `shell.modality` is filled here, not just `design.tokens`, because a PERSISTED
 * blob is not guaranteed to carry it — a channel saved before the field existed,
 * or any partially-written blob, comes back with a `shell` that has no modality.
 * Left undefined it took out three things at once: the preview showed no selected
 * modality, the boot effect early-returned on a falsy `bootModality` so the stage
 * stayed blank, and the Shell picker rendered with nothing chosen. A modality is
 * not optional to the widget — `createDefaultShellSection()` says floating — so
 * it should not be optional here either.
 *
 * Applied to the server snapshot AND the local one, so filling it cannot make the
 * form look dirty on load.
 */
export function withResolvedDefaults(
  config: WidgetAppConfig,
  /**
   * The channel's name, used to seed an unset `branding.displayName`.
   *
   * `customerName` was retired (2026-09-21) and `displayName` is its only
   * successor — but a channel designed before that has neither, and the bot side
   * would silently fall back to generic copy on six surfaces ("Bot" in the a11y
   * prefix, `engageWithUs`, `menu`, logo-only). Seeding turns a silent
   * regression into a pre-filled field the designer can accept or edit.
   *
   * Applied to the server snapshot AND the local one, so the seed cannot make an
   * untouched form look dirty. Nothing is saved until the designer edits
   * something — and if they do, this is the value that lands.
   */
  channelName?: string
): WidgetAppConfig {
  const tokenDefaults = readThemeDefaults(config.design.theme || 'light');
  const defaults = createDefaultShellSection();
  // Also clamps a modality the widget could not boot (a hand-edited blob, or one
  // written by a newer Pro Studio) back to something renderable.
  const modality = MODALITIES.includes(config.shell?.modality)
    ? config.shell.modality
    : defaults.modality;

  return {
    ...config,
    design: {
      ...config.design,
      tokens: { ...tokenDefaults, ...config.design.tokens },
    },
    shell: { ...defaults, ...config.shell, modality },
    branding: {
      ...config.branding,
      // Falsy, not nullish: `''` is what an untouched blob carries, and it is
      // exactly the case that needs seeding.
      displayName: config.branding?.displayName || channelName || '',
    },
  };
}

/**
 * What a save sends, and the baseline the form compares against afterwards.
 *
 * The seed runs BEFORE sending, not only on the baseline: seeding just the
 * baseline turned a cleared title into the channel name there while the form
 * kept `''`, so a successful save left the form dirty.
 */
export function prepareDesignSave(
  config: WidgetAppConfig,
  channelName?: string
): { toSave: WidgetAppConfig; baseline: WidgetAppConfig } {
  const toSave = withResolvedDefaults(config, channelName);
  return {
    toSave,
    baseline: withResolvedDefaults(projectOwnedBlob(toSave), channelName),
  };
}
